import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { User2, Shirt } from "lucide-react";

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialRole = params.get("role") === "provider" ? "provider" : "customer";

  const [role, setRole] = useState(initialRole);
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "", city: "", zip: "", address: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { setRole(initialRole); }, [initialRole]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const r = await register({ ...form, role });
    setLoading(false);
    if (!r.ok) {
      setError(r.error);
      toast.error(r.error);
      return;
    }
    toast.success("Registrace proběhla úspěšně!");
    navigate(role === "provider" ? "/dashboard/provider" : "/dashboard/customer");
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-1 container-page py-16 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="tiny-label mb-3">Vytvořte si účet</div>
          <h1 className="font-display text-4xl xl:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
            Registrace zdarma za <span className="text-sky-600">60 sekund</span>.
          </h1>
          <p className="mt-5 text-slate-600 max-w-md">
            Vyberte si svůj typ účtu – zákazník (chcete prát) nebo prádelna (chcete pomoct).
          </p>

          <div className="mt-8 grid grid-cols-2 gap-3 max-w-md">
            <button
              type="button"
              onClick={() => setRole("customer")}
              data-testid="register-role-customer"
              className={`p-5 rounded-2xl border-2 text-left transition-all ${role === "customer" ? "border-sky-600 bg-sky-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
            >
              <User2 className={`w-6 h-6 ${role === "customer" ? "text-sky-600" : "text-slate-500"}`} />
              <div className="font-display font-bold mt-3">Zákazník</div>
              <div className="text-xs text-slate-500 mt-1">Chci si nechat vyprat prádlo</div>
            </button>
            <button
              type="button"
              onClick={() => setRole("provider")}
              data-testid="register-role-provider"
              className={`p-5 rounded-2xl border-2 text-left transition-all ${role === "provider" ? "border-sky-600 bg-sky-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
            >
              <Shirt className={`w-6 h-6 ${role === "provider" ? "text-sky-600" : "text-slate-500"}`} />
              <div className="font-display font-bold mt-3">Prádelna</div>
              <div className="text-xs text-slate-500 mt-1">Nabízím praní za odměnu</div>
            </button>
          </div>
        </div>

        <Card className="lg:col-span-7 p-8">
          <form onSubmit={submit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">{role === "provider" ? "Vaše jméno / název" : "Jméno a příjmení"}</Label>
                <Input id="name" required value={form.name} onChange={update("name")} data-testid="register-name" />
              </div>
              <div>
                <Label htmlFor="phone">Telefon</Label>
                <Input id="phone" required value={form.phone} onChange={update("phone")} placeholder="+420 …" data-testid="register-phone" />
              </div>
            </div>

            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={update("email")} data-testid="register-email" />
            </div>

            <div>
              <Label htmlFor="password">Heslo (min 6 znaků)</Label>
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={update("password")} data-testid="register-password" />
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <Label htmlFor="city">{role === "provider" ? "Město působnosti" : "Vaše město"}</Label>
                <Input id="city" required value={form.city} onChange={update("city")} placeholder="Praha" data-testid="register-city" />
              </div>
              <div>
                <Label htmlFor="zip">PSČ</Label>
                <Input id="zip" value={form.zip} onChange={update("zip")} placeholder="11000" data-testid="register-zip" />
              </div>
            </div>

            {role === "customer" && (
              <div>
                <Label htmlFor="address">Adresa pro vyzvednutí</Label>
                <Input id="address" value={form.address} onChange={update("address")} placeholder="Ulice a č.p." data-testid="register-address" />
              </div>
            )}

            {error && <div className="text-sm text-rose-600">{error}</div>}

            <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit">
              {loading ? "Vytvářím účet…" : `Zaregistrovat se jako ${role === "provider" ? "prádelna" : "zákazník"}`}
            </Button>
          </form>

          <div className="mt-5 text-sm text-slate-600">
            Už máte účet?{" "}
            <Link to="/prihlaseni" className="text-sky-600 font-semibold hover:underline" data-testid="register-to-login">
              Přihlásit se
            </Link>
          </div>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

export default Register;
